import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { 
  Box, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Drawer, 
  Tooltip,
  IconButton
} from '@mui/material';
import { motion } from 'framer-motion';

// Icons
import HomeIcon from '@mui/icons-material/Home';
import ListAltIcon from '@mui/icons-material/ListAlt';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';

// Auth Context
import { useAuth } from '../../contexts/AuthContext';
// Profile Component
import UserProfile from '../profile/UserProfile';

const drawerWidth = 140; // Reduced from 180 for better balance

const SidebarContainer = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
    backgroundColor: 'rgba(255, 255, 255, 0.95)', // Light theme with white background
    backdropFilter: 'blur(20px)',
    borderRight: '1px solid rgba(0, 153, 153, 0.15)',
    boxShadow: '4px 0 20px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: theme.spacing(3, 1),
  },
}));

const SidebarIcon = styled(motion.div)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '14px',
  padding: '12px',
  cursor: 'pointer',
  '& .MuiSvgIcon-root': {
    fontSize: '28px', // Make icons bigger
  },
});

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [tooltips, setTooltips] = useState({});
  const [profileOpen, setProfileOpen] = useState(false);

  // Define nav items
  const navItems = [
    { 
      label: 'Dashboard', 
      icon: <HomeIcon />, 
      path: '/dashboard', 
      tooltip: 'Dashboard',
      id: 'dashboard'
    },
    { 
      label: 'Apply', 
      icon: <ListAltIcon />, 
      path: '/nominate', 
      tooltip: 'Apply',
      id: 'nominate'
    },
    { 
      label: 'Vote', 
      icon: <HowToVoteIcon />, 
      path: '/vote', 
      tooltip: 'Vote',
      id: 'vote'
    },
  ];

  // Add profile item for all users
  navItems.push({
    label: 'Profile', 
    icon: <AccountCircleIcon />, 
    action: () => setProfileOpen(true),
    tooltip: 'User Profile',
    id: 'profile'
  });

  // Add admin console for admin users
  if (user && user.role === 'admin') {
    navItems.push({
      label: 'Admin', 
      icon: <SettingsIcon />, 
      path: '/admin', 
      tooltip: 'Admin Console',
      id: 'admin'
    });
  }

  const handleTooltipOpen = (id) => {
    setTooltips(prev => ({ ...prev, [id]: true }));
  };

  const handleTooltipClose = (id) => {
    setTooltips(prev => ({ ...prev, [id]: false }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <SidebarContainer variant="permanent">
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ mt: 1, mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Box 
            component="img"
            src="/logo.svg"
            alt="Company Logo"
            sx={{ 
              width: '60px',
              height: 'auto',
              filter: 'drop-shadow(0px 2px 5px rgba(0,0,0,0.2))'
            }}
          />
        </Box>
        <List sx={{ p: 0, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem
              key={item.path || item.id}
              disablePadding
              sx={{ my: 1 }}
            >
              <Tooltip 
                title={item.tooltip} 
                placement="right" 
                open={tooltips[item.id]}
                onClose={() => handleTooltipClose(item.id)}
                disableFocusListener
                disableTouchListener
              >
                <SidebarIcon
                  onClick={() => item.action ? item.action() : navigate(item.path)}
                  onMouseEnter={() => handleTooltipOpen(item.id)}
                  onMouseLeave={() => handleTooltipClose(item.id)}
                  animate={{ 
                    backgroundColor: isActive ? '#009999' : 'transparent',
                    color: isActive ? '#fff' : 'rgba(74, 85, 104, 0.8)',
                  }}
                  whileHover={{ 
                    scale: 1.1, 
                    backgroundColor: isActive ? '#009999' : 'rgba(74, 85, 104, 0.1)',
                  }}
                  transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                >
                  {item.icon}
                </SidebarIcon>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton 
            onClick={handleLogout} 
            sx={{ 
              color: 'rgba(74, 85, 104, 0.8)',
              '&:hover': { 
                color: '#1a202c',
                backgroundColor: 'rgba(74, 85, 104, 0.1)'
              }
            }}
          >
            <LogoutIcon />
          </IconButton>
        </Box>
      </Box>
      
      {/* User Profile Modal */}
      <UserProfile 
        open={profileOpen} 
        onClose={() => setProfileOpen(false)} 
      />
    </SidebarContainer>
  );
};

export default Sidebar;
